import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, AUTH_ERROR_KEY } from '@/hooks/useAuth'
import { Spinner } from './Spinner'

/**
 * On mobile, Google/Apple sign-in returns the user to the app via a full-page
 * redirect. This gate runs once at app start: if a redirect flow is pending it
 * finishes it (create profile · migrate posts · claim tag) and routes to the
 * right place, showing a loader meanwhile so the user never flashes the wrong
 * screen. On desktop (popup) nothing is pending and it renders through.
 */
export const OAuthRedirectGate = ({ children }: { children: ReactNode }) => {
  const { finishRedirect, hasPendingRedirect } = useAuth()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(() => hasPendingRedirect())

  useEffect(() => {
    if (!hasPendingRedirect()) return
    let cancelled = false
    void finishRedirect().then((outcome) => {
      if (cancelled) return
      if (outcome?.status === 'created') {
        navigate(`/profile-setup?next=${encodeURIComponent(outcome.next)}`, { replace: true })
      } else if (outcome?.status === 'existing' || outcome?.status === 'signedin') {
        navigate(outcome.next, { replace: true })
      } else if (outcome?.status === 'error') {
        try {
          sessionStorage.setItem(AUTH_ERROR_KEY, outcome.message)
        } catch {
          /* ignore */
        }
        navigate('/account', { replace: true })
      }
      // outcome === null (user backed out): fall through to the current route.
      setBusy(false)
    })
    return () => {
      cancelled = true
    }
    // Run once on mount — the pending flag is consumed inside finishRedirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (busy) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ink-900">
        <Spinner size={80} label="Finishing sign-in" />
      </div>
    )
  }
  return <>{children}</>
}
